// Section URL segments are localized: Georgian users get Georgian words in
// the path (e.g. /ka/ლოკაციები/xyz), English keeps the English word. Every
// non-home route in src/App.jsx has an entry here (or in SUBSECTIONS for a
// nested static segment like adventures/route or culture/article) so that
// route definitions, all Link/navigate call sites, the language switcher,
// and the sitemap generator stay in agreement.
//
// Plain JS (no React/JSX) so this can be imported both by React components
// and directly by scripts/generate-sitemap.js (a standalone Node script).
export const SECTIONS = {
  categories: { en: "categories", ka: "კატეგორიები" },
  place: { en: "place", ka: "ლოკაციები" },
  about: { en: "about", ka: "ჩვენს-შესახებ" },
  adventures: { en: "adventures", ka: "თავგადასავლები" },
  culture: { en: "culture", ka: "კულტურა" },
  guide: { en: "guide", ka: "გზამკვლევი" },
  "photo-guide": { en: "photo-guide", ka: "ფოტო-გზამკვლევი" },
  cuisine: { en: "cuisine", ka: "სამზარეულო" },
  festivals: { en: "festivals", ka: "დღესასწაულები" },
  "local-guides": { en: "local-guides", ka: "ადგილობრივი-გიდები" },
  hotels: { en: "hotels", ka: "სასტუმროები" },
};

// Static sub-segments nested one level under a section.
export const SUBSECTIONS = {
  route: { en: "route", ka: "მარშრუტი" },
  article: { en: "article", ka: "სტატია" },
};

const ALL = { ...SECTIONS, ...SUBSECTIONS };

// Builds a localized link path with real values, e.g.:
//   sectionPath("ka", "place", id) -> "/ka/ლოკაციები/<id>"
//   sectionPath("ka", ["adventures", "route"], id) -> "/ka/თავგადასავლები/მარშრუტი/<id>"
export function sectionPath(lang, key, ...rest) {
  const keys = Array.isArray(key) ? key : [key];
  const segs = keys.map((k) => ALL[k]?.[lang] || k);
  const parts = [lang, ...segs, ...rest.filter((p) => p !== undefined && p !== null && p !== "")];
  return "/" + parts.join("/");
}

// Builds a react-router <Route path> literal for a canonical (non-legacy)
// route. The leading segment is always the literal ":lang" token (not the
// `lang` value itself) so every page keeps getting a real :lang route param
// — components like LanguageSync read useParams().lang to sync app state,
// and that must keep working regardless of which section-word language this
// particular route pattern was built for. `lang` here only selects which
// language's section wording to use, e.g.:
//   sectionRoutePath("en", "place", "id") -> "/:lang/place/:id"
//   sectionRoutePath("ka", ["adventures", "route"], "id") -> "/:lang/თავგადასავლები/მარშრუტი/:id"
export function sectionRoutePath(lang, key, paramName) {
  const keys = Array.isArray(key) ? key : [key];
  const segs = keys.map((k) => ALL[k]?.[lang] || k);
  const parts = [":lang", ...segs, ...(paramName ? [`:${paramName}`] : [])];
  return "/" + parts.join("/");
}

// Same as sectionRoutePath but always using the English segment words,
// regardless of `lang` — used to build legacy redirect routes so old
// /ka/<english-segment>/... links (already shared/indexed) keep working.
export function legacyEnRoutePath(lang, key, paramName) {
  const keys = Array.isArray(key) ? key : [key];
  const segs = keys.map((k) => ALL[k]?.en || k);
  const parts = [lang, ...segs, ...(paramName ? [`:${paramName}`] : [])];
  return "/" + parts.join("/");
}

// Reverse-lookup: which section/subsection key does this URL segment belong
// to, regardless of which language it's written in.
function keyForSegment(segment) {
  for (const [key, val] of Object.entries(ALL)) {
    if (val.en === segment || val.ka === segment) return key;
  }
  return null;
}

// Rewrites a full pathname (e.g. location.pathname) into the target
// language, translating both the /:lang prefix and any localized section
// segments. Falls back to a plain prefix swap for paths it doesn't
// recognize (e.g. /admin, or a future route not yet added to SECTIONS).
export function translatePath(pathname, targetLang) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "/" + targetLang;
  const [, ...rest] = parts;
  if (rest.length === 0) return "/" + targetLang;

  const key1 = keyForSegment(rest[0]);
  if (!key1) return "/" + [targetLang, ...rest].join("/");

  const out = [targetLang, ALL[key1][targetLang]];
  let consumed = 1;
  if (rest[1]) {
    const key2 = keyForSegment(rest[1]);
    if (key2 && SUBSECTIONS[key2]) {
      out.push(SUBSECTIONS[key2][targetLang]);
      consumed = 2;
    }
  }
  out.push(...rest.slice(consumed));
  return "/" + out.join("/");
}
